import { gainToDb } from 'tone';
import { clamp, lerp, normalize, roundToStep } from '$utils/math';

export class ParameterHandler {
    constructor(description, selector, updater) {
        this.description = description;
        this.selector = selector;
        this.updater = value => {
            this.notifyUpdateSubscribers(value);
            return updater(value);
        };
        this.subscribers = new Set();
    }

    subscribeToUpdate(subscriber) {
        this.subscribers.add(subscriber);
        return () => {
            this.subscribers.delete(subscriber);
        }
    }

    notifyUpdateSubscribers(value) {
        this.subscribers.forEach(subscriber => subscriber(value));
    }

    ensureValidValue = value => {
        if (this.description.type === 'float') {
            return clamp(value, this.description.min, this.description.max);
        } else if (this.description.type === 'enum') {
            return ensureValueInOptions(value, this.description.options, this.getDefaultValue());
        }
        return value;
    }

    num2Real = numValue => {
        const discreteValue = roundToStep(numValue, this.getStep());
        switch(this.description.type) {
            case 'float':
            case 'int':
                return discreteValue;
            case 'enum':
                return this.description.options[discreteValue];
            default: this.throwUnknownParameterError();
        }
    }

    real2Num = realValue => {
        switch(this.description.type) {
            case 'float':
            case 'int':
                return realValue;
            case 'enum':
                return this.description.options.indexOf(realValue);
            default: this.throwUnknownParameterError();
        }
    }

    norm2Num = normValue => {
        const numMin = this.getNumericMin();
        const numMax = this.getNumericMax();
        const exponent = this.description.logarithmExponent ?? 2;

        if(!this.description.logarithmic) return lerp(numMin, numMax, normValue);
        return lerp(numMin, numMax, Math.pow(normValue, exponent));
    }

    num2Norm = numValue => {
        const numMin = this.getNumericMin();
        const numMax = this.getNumericMax();
        const exponent = this.description.logarithmExponent ?? 2;

        if(!this.description.logarithmic) return normalize(numValue, numMin, numMax);
        return Math.pow(normalize(numValue, numMin, numMax), 1/exponent);
    }

    real2String = realValue => {
        let displayValue = realValue;

        if (this.description.type === 'float') {
            const valueTenExponent = Math.floor(realValue) === 0 ? 1 : Math.floor(Math.log10(Math.abs(realValue))) + 1; // the number of digits of the integer
            const stepSize = this.getStep();
            const stepDecimals = stepSize ? stepSize.toString().split('.')[1]?.length || 0 : undefined;
            const precision = 4; // but integers can have more ciphers
            let maxDecimals = 2; // for continous values, we show a maximum of 2 decimals.
            if (stepSize) maxDecimals = stepDecimals; // for stepped ones, the ones corresponding to the step size.
            if (this.description.display === "percent") maxDecimals -= 2; // because we'll multiply by 100!
            const decimals = this.description.display === "db" ? 1 : clamp(precision - valueTenExponent, 0, maxDecimals);
            if (this.description.display === "db") {
                displayValue = gainToDb(displayValue);
            } else if (this.description.display === "percent") {
                displayValue *= 100;
            }
            displayValue = displayValue.toFixed(decimals);
            displayValue = displayValue.toString().replace("Infinity", "∞");
        }

        const THINSPACE = " ";
        if (this.description.unit) displayValue += THINSPACE + this.description.unit;
        if (this.description.display === "percent") displayValue += THINSPACE + "%"
        return displayValue;
    }

    getNumericMin = () => {
        switch(this.description.type) {
            case 'float':
            case 'int':
                return this.description.min;
            case 'enum':
                return 0;
            default: this.throwUnknownParameterError();
        }
    }

    getNumericMax = () => {
        switch(this.description.type) {
            case 'float':
            case 'int':
                return this.description.max;
            case 'enum':
                return this.description.options.length - 1;
            default: this.throwUnknownParameterError();
        }
    }

    getStep = () => {
        switch(this.description.type) {
            case 'float':
            case 'int':
                return this.description.step;
            case 'enum':
                return 1;
            default:
                throw this.throwUnknownParameterError();
        }
    }

    // cardinality == number of options. kinda.
    getCardinality = () => Math.floor((this.getNumericMax() - this.getNumericMin() + (this.getStep() ?? 0)) / (this.getStep() ?? 0));

    throwUnknownParameterError = () => {
        throw new Error(`Unknown parameter type (${this.description.type}) for parameter ${this.description.nom}`);
    }

    getDefaultValue = () => this.description.initial;
}

// Make sure enum value is within options
const ensureValueInOptions = (value, options, defaultValue) => {
    if (options.includes(value)) return value;
    if (defaultValue != null) return defaultValue;
    return options[0];
}
